import React, { useMemo, useState } from "react";
import { TrendingDown, Info, Calculator } from "lucide-react";
import { Slider } from "../ui/slider";
import { useI18n } from "../../i18n/I18nContext";

const CURRENCIES = {
  EUR: { symbol: "€", code: "EUR", locale: "nl-NL", flag: "🇪🇺" },
  USD: { symbol: "$", code: "USD", locale: "en-US", flag: "🇺🇸" },
  GBP: { symbol: "£", code: "GBP", locale: "en-GB", flag: "🇬🇧" },
};

export const RevenueCalculator = () => {
  const { t } = useI18n();
  const [missed, setMissed] = useState(20);
  const [conv, setConv] = useState(30);
  const [value, setValue] = useState(350);
  const [currency, setCurrency] = useState("EUR");

  const cur = CURRENCIES[currency];
  const fmt = (n) =>
    new Intl.NumberFormat(cur.locale, {
      style: "currency",
      currency: cur.code,
      maximumFractionDigits: 0,
    }).format(Math.max(0, Math.round(n || 0)));

  const monthly = useMemo(() => missed * (conv / 100) * value * 4.3, [missed, conv, value]);
  const yearly = monthly * 12;

  return (
    <section id="calculator" className="py-12 lg:py-20 relative" data-testid="calculator-section">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="flex items-start justify-between gap-6 mb-8 flex-wrap">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-3">
              <Calculator size={14} className="text-cyan-300" />
              <div className="eyebrow">{t.calc.eyebrow}</div>
            </div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight">
              {t.calc.title}
            </h2>
            <p className="text-slate-400 mt-4 text-lg">{t.calc.sub}</p>
          </div>

          {/* Currency selector */}
          <div className="glass rounded-xl p-1.5 flex gap-1" data-testid="currency-selector" role="tablist" aria-label={t.calc.currencyLabel}>
            {Object.entries(CURRENCIES).map(([code, c]) => (
              <button
                key={code}
                type="button"
                onClick={() => setCurrency(code)}
                data-testid={`currency-${code}`}
                aria-pressed={currency === code}
                className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-mono font-semibold transition-colors ${currency === code ? "bg-blue-600 text-white shadow-[0_0_18px_rgba(37,99,235,0.4)]" : "text-slate-300 hover:text-white hover:bg-white/5"}`}
              >
                <span aria-hidden>{c.flag}</span>
                <span>{c.symbol}</span>
                <span className="hidden sm:inline">{code}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="grid lg:grid-cols-5 gap-6">
          {/* Inputs */}
          <div className="lg:col-span-3 glass rounded-2xl p-6 sm:p-7 space-y-7" data-testid="calc-inputs">
            <SliderRow
              label={t.calc.missedCalls}
              value={missed}
              displayValue={`${missed} / week`}
              min={1}
              max={100}
              step={1}
              onChange={setMissed}
              testid="slider-missed"
            />

            <SliderRow
              label={t.calc.conv}
              value={conv}
              displayValue={`${conv}%`}
              min={1}
              max={100}
              step={1}
              onChange={setConv}
              testid="slider-conv"
            />

            <div>
              <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                <label className="text-sm text-slate-300 font-medium">{t.calc.avgValue}</label>
                <div className="flex items-center bg-black/30 border border-white/10 focus-within:border-blue-400 rounded-lg pl-3 pr-1 py-1">
                  <span className="text-slate-400 text-sm mr-1">{cur.symbol}</span>
                  <input
                    type="number"
                    inputMode="numeric"
                    min={0}
                    max={5000}
                    step={10}
                    value={value}
                    onChange={(e) => setValue(Math.max(0, Number(e.target.value) || 0))}
                    className="w-24 bg-transparent text-white font-mono font-semibold text-right outline-none"
                    data-testid="input-value"
                  />
                </div>
              </div>
              <Slider
                value={[value]}
                min={0}
                max={2000}
                step={10}
                onValueChange={(v) => setValue(v[0])}
                data-testid="slider-value"
              />
            </div>

            <div className="pt-4 border-t border-white/10 flex items-start gap-3 text-xs text-slate-400 leading-relaxed">
              <Info size={14} className="text-cyan-300 mt-0.5 shrink-0" />
              <span>{t.calc.helper}</span>
            </div>
          </div>

          {/* Results */}
          <div className="lg:col-span-2 flex flex-col gap-4">
            <div className="glass-strong rounded-2xl p-7 relative overflow-hidden" data-testid="calc-result-monthly">
              <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-red-500/15 blur-3xl" />
              <div className="flex items-center gap-2 text-red-300">
                <TrendingDown size={14} />
                <div className="eyebrow !text-red-300">{t.calc.monthly}</div>
              </div>
              <div className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl mt-3 bg-clip-text text-transparent bg-gradient-to-b from-red-200 to-red-500 leading-none break-words">
                {fmt(monthly)}
              </div>
            </div>

            <div className="glass rounded-2xl p-7" data-testid="calc-result-yearly">
              <div className="flex items-center gap-2 text-red-300">
                <TrendingDown size={14} />
                <div className="eyebrow !text-red-300">{t.calc.yearly}</div>
              </div>
              <div className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl mt-3 text-white leading-none break-words">
                {fmt(yearly)}
              </div>
              <p className="text-[11px] text-slate-500 mt-4 leading-relaxed italic">
                {t.calc.disclaimer}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const SliderRow = ({ label, value, displayValue, min, max, step, onChange, testid }) => (
  <div>
    <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
      <label className="text-sm text-slate-300 font-medium">{label}</label>
      <span className="font-mono text-white font-semibold text-base">{displayValue}</span>
    </div>
    <Slider
      value={[value]}
      min={min}
      max={max}
      step={step}
      onValueChange={(v) => onChange(v[0])}
      data-testid={testid}
    />
    <div className="flex justify-between mt-1.5 text-[10px] text-slate-500 font-mono">
      <span>{min}</span>
      <span>{max}</span>
    </div>
  </div>
);
